import React, { useEffect, useState } from "react";
import { Sparkles, ArrowRight, Construction, CheckCircle2, Check } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { api } from "@/lib/api";

// Modules réellement à venir (les fonctionnalités livrées sont listées plus bas)
const FUTURE_MODULES = [
  { id: "calsync", eta: "Q1 2026" },
  { id: "social", eta: "Q1 2026" },
  { id: "multi", eta: "Q2 2026" },
  { id: "workflows", eta: "Q2 2026" },
  { id: "emr", eta: "Q4 2026" },
];

// Déjà disponibles dans Solunary (langue-neutres : noms de produits/fonctionnalités)
const SHIPPED = [
  "Solunary Care", "Solunary Learn",
  "Calendar (month/week/day + drag & drop)", "Stripe Connect", "Care modalities",
  "Boosters & packages", "Custom domain",
];

export default function ComingSoonAdmin() {
  const t = useT("admin");
  // The server, not client memory, is the source of truth for "did THIS practice
  // already vote" — populated from GET /tenant/roadmap/votes on mount so a reload
  // shows reality (BUG-213).
  const [votedIds, setVotedIds] = useState(() => new Set());
  const [pendingIds, setPendingIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/tenant/roadmap/votes");
        if (!cancelled) setVotedIds(new Set(data?.voted_module_ids || []));
      } catch (e) {
        // Nothing votable to show as "already voted" is a safe default — the vote
        // endpoint itself still enforces one-per-practice server-side either way.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const vote = async (moduleId, title) => {
    if (votedIds.has(moduleId) || pendingIds.has(moduleId)) return;
    setPendingIds((prev) => new Set(prev).add(moduleId));
    try {
      const { data } = await api.post("/tenant/roadmap/vote", { module_id: moduleId });
      setVotedIds((prev) => new Set(prev).add(moduleId));
      toast.success(data?.already_voted ? t("coming.alreadyVoted") : t("coming.voteToast", { title }));
    } catch (e) {
      toast.error(t("coming.voteError"));
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(moduleId);
        return next;
      });
    }
  };
  return (
    <div data-testid="coming-soon" className="space-y-8 max-w-5xl">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <Construction className="w-6 h-6 text-sol-gold" /> {t("coming.title")}
        </h1>
        <p className="text-eh-muted mt-2">
          {t("coming.subtitle")}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FUTURE_MODULES.map((m) => {
          const title = t(`coming.${m.id}.title`);
          const hasVoted = votedIds.has(m.id);
          const isPending = pendingIds.has(m.id);
          return (
            <div
              key={m.id}
              data-testid={`future-${m.id}`}
              className="bg-gradient-to-br from-white to-sol-ivory/40 border border-eh-border-soft rounded-2xl p-6 hover:shadow-md transition flex flex-col"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-wider text-sol-gold font-semibold">{t(`coming.${m.id}.cat`)}</span>
                <span className="text-xs text-eh-muted font-mono">{m.eta}</span>
              </div>
              <h3 className="font-sol font-semibold text-lg text-sol-midnight mb-2">{title}</h3>
              <p className="text-sm text-eh-muted leading-relaxed mb-4 flex-1">{t(`coming.${m.id}.desc`)}</p>
              <button
                className={`text-xs flex items-center gap-1 self-start ${hasVoted ? "text-emerald-700 cursor-default" : "text-sol-midnight/60 hover:text-sol-midnight disabled:opacity-50"}`}
                onClick={() => vote(m.id, title)}
                disabled={hasVoted || isPending}
                data-testid={`vote-${m.id}`}
                data-voted={hasVoted ? "true" : "false"}
              >
                {hasVoted ? (
                  <><Check className="w-3 h-3" /> {t("coming.alreadyVoted")}</>
                ) : (
                  <><Sparkles className="w-3 h-3" /> {t("coming.vote")} <ArrowRight className="w-3 h-3" /></>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-eh-border-soft rounded-2xl p-6" data-testid="shipped-list">
        <h2 className="font-display text-xl text-eh-ink mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" /> {t("coming.shipped")}
        </h2>
        <div className="flex flex-wrap gap-2">
          {SHIPPED.map((s) => (
            <span key={s} className="text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-full">{s}</span>
          ))}
        </div>
      </div>

      <div className="bg-sol-midnight text-sol-ivory rounded-3xl p-8 text-center">
        <Sparkles className="w-6 h-6 text-sol-gold mx-auto mb-3" />
        <h2 className="font-display text-2xl mb-2">{t("coming.ideaTitle")}</h2>
        <p className="text-sol-ivory/60 mb-5 text-sm max-w-xl mx-auto">
          {t("coming.ideaText")}
        </p>
        <a
          href="mailto:hey@solunary.health?subject=Idea"
          className="inline-block bg-sol-gold hover:bg-sol-gold/90 text-sol-midnight px-6 py-3 rounded-2xl text-sm font-semibold"
          data-testid="suggest-feature"
        >
          {t("coming.suggest")}
        </a>
      </div>
    </div>
  );
}
